import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { lessonSchedulesApi } from '@/api/lessonSchedules'
import { validateSchedulePauses } from '@/components/lessonSchedule/schedulePauseValidation'
import { getErrorMessage } from '@/utils/error'
import type { LessonSchedulePause, LessonScheduleSlot, LessonScheduleTemplate } from '@/types'

const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 7] as const

export interface UserLessonScheduleEditModalProps {
  open: boolean
  onClose: () => void
  userId: number
  /** Текущее расписание ученика (индивидуальное); при null создаётся новое */
  schedule: LessonScheduleTemplate | null
  onSaved: () => void | Promise<void>
}

/**
 * Редактирование расписания ученика в модальном окне (аналогично группе на GroupDetailsPage).
 */
export function UserLessonScheduleEditModal({
  open,
  onClose,
  userId,
  schedule,
  onSaved,
}: UserLessonScheduleEditModalProps) {
  const { t } = useTranslation()
  const [editorError, setEditorError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editorSlots, setEditorSlots] = useState<LessonScheduleSlot[]>([])
  const [editorPauses, setEditorPauses] = useState<LessonSchedulePause[]>([])

  const weekdayLabels = useMemo(
    () =>
      ({
        1: t('lessons.weekday1', 'Понедельник'),
        2: t('lessons.weekday2', 'Вторник'),
        3: t('lessons.weekday3', 'Среда'),
        4: t('lessons.weekday4', 'Четверг'),
        5: t('lessons.weekday5', 'Пятница'),
        6: t('lessons.weekday6', 'Суббота'),
        7: t('lessons.weekday7', 'Воскресенье'),
      }) as Record<number, string>,
    [t]
  )

  useEffect(() => {
    if (!open) return
    setEditorError(null)
    if (schedule) {
      setEditorSlots((schedule.slots ?? []).map((s) => ({ ...s })))
      setEditorPauses((schedule.pauses ?? []).map((p) => ({ ...p })))
    } else {
      setEditorSlots([])
      setEditorPauses([])
    }
  }, [open, schedule])

  const handleSave = async () => {
    if (!Number.isFinite(userId)) return
    setEditorError(null)

    const pausesErrorKey = validateSchedulePauses(editorPauses)
    if (pausesErrorKey) {
      setEditorError(t(pausesErrorKey))
      return
    }

    setSaving(true)
    try {
      const upserted = await lessonSchedulesApi.upsertUserSchedule(userId, {
        is_active: schedule?.is_active ?? true,
      })

      const slotsPayload = editorSlots.map((s) => ({
        weekday: s.weekday,
        start_time: s.start_time.includes(':') ? s.start_time : `${s.start_time}:00`,
        end_time: s.end_time.includes(':') ? s.end_time : `${s.end_time}:00`,
      }))
      await lessonSchedulesApi.updateUserSlots(userId, upserted.id, slotsPayload)

      const existingPauseIds = new Set((schedule?.pauses ?? []).map((p) => p.id))
      await lessonSchedulesApi.updateUserPauses(
        userId,
        upserted.id,
        editorPauses.map((p) => ({
          id: existingPauseIds.has(p.id) ? p.id : undefined,
          date_from: p.date_from,
          date_to: p.date_to,
          reason: p.reason ?? null,
        }))
      )

      await onSaved()
      onClose()
    } catch (e: unknown) {
      setEditorError(getErrorMessage(e) || t('lessonSchedules.saveError', 'Ошибка сохранения расписания'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{t('lessonSchedules.editScheduleTitle', 'Редактирование расписания')}</DialogTitle>
      <DialogContent>
        {editorError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {editorError}
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          {t('lessons.slotsTitle', 'Слоты')}
        </Typography>
        {editorSlots.length === 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t(
              'lessonSchedules.emptySlotsHint',
              'Можно сохранить расписание без слотов — автоматически уроки создаваться не будут.'
            )}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {editorSlots.map((s, idx) => (
            <Box
              key={`${s.id}-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px 180px 180px 1fr 40px' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControl size="small">
                <InputLabel>{t('lessons.weekday', 'День недели')}</InputLabel>
                <Select
                  label={t('lessons.weekday', 'День недели')}
                  value={s.weekday}
                  onChange={(e) => {
                    const next = [...editorSlots]
                    next[idx] = { ...s, weekday: Number(e.target.value) }
                    setEditorSlots(next)
                  }}
                >
                  {WEEKDAY_ORDER.map((w) => (
                    <MenuItem key={w} value={w}>
                      {weekdayLabels[w]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="time"
                value={s.start_time ? s.start_time.slice(0, 5) : '00:00'}
                label={t('lessons.startTime', 'Начало')}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const next = [...editorSlots]
                  next[idx] = { ...s, start_time: `${e.target.value}:00` }
                  setEditorSlots(next)
                }}
              />
              <TextField
                size="small"
                type="time"
                value={s.end_time ? s.end_time.slice(0, 5) : '00:00'}
                label={t('lessons.endTime', 'Конец')}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const next = [...editorSlots]
                  next[idx] = { ...s, end_time: `${e.target.value}:00` }
                  setEditorSlots(next)
                }}
              />

              <IconButton onClick={() => setEditorSlots(editorSlots.filter((_, i) => i !== idx))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => {
              setEditorSlots((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  weekday: 1,
                  start_time: '09:00:00',
                  end_time: '10:00:00',
                },
              ])
            }}
          >
            {t('lessons.addSlot', 'Добавить слот')}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          {t('lessons.pausesTitle', 'Паузы / отпуска')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {editorPauses.map((p, idx) => (
            <Box
              key={`${p.id}-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '160px 160px 1fr 40px' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                type="date"
                value={p.date_from}
                label={t('lessons.dateFrom', 'С')}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const next = [...editorPauses]
                  next[idx] = { ...p, date_from: e.target.value }
                  setEditorPauses(next)
                }}
              />
              <TextField
                size="small"
                type="date"
                value={p.date_to}
                label={t('lessons.dateTo', 'По')}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const next = [...editorPauses]
                  next[idx] = { ...p, date_to: e.target.value }
                  setEditorPauses(next)
                }}
              />
              <TextField
                size="small"
                value={p.reason ?? ''}
                label={t('lessons.pauseReason', 'Причина')}
                onChange={(e) => {
                  const next = [...editorPauses]
                  next[idx] = { ...p, reason: e.target.value || null }
                  setEditorPauses(next)
                }}
              />
              <IconButton onClick={() => setEditorPauses(editorPauses.filter((_, i) => i !== idx))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => {
              const today = new Date().toISOString().slice(0, 10)
              setEditorPauses((prev) => [
                ...prev,
                { id: Date.now(), date_from: today, date_to: today, reason: null },
              ])
            }}
          >
            {t('lessons.addPause', 'Добавить паузу')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Отмена')}
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {t('common.save', 'Сохранить')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
